// components/ActionMenu.jsx
'use client';
import { useState, useRef } from 'react';
import { Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Box } from '@mui/material';
import { EditOutlined as EditIcon, MoreVert as MoreVertIcon, VisibilityOutlined as ViewIcon } from '@mui/icons-material';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';

const ActionMenu = ({
  row,
  onView,
  onEdit,
  onDelete,
  mode = '',
  extraActions = [],
  disableView = false,
  disableEdit = false,
  disableDelete = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const iconRef = useRef(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  if (mode === 'inline') {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', py: 0.5, height: '100%' }}>
        {extraActions?.map((a, i) => (
          <Tooltip title={a.label} key={i}>
            <IconButton size="small" onClick={() => a.onClick(row)} color="inherit">
              {a.icon}
            </IconButton>
          </Tooltip>
        ))}

        {onView && (
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => onView(row.id, row)}
              disabled={disableView}
              sx={{
                bgcolor: disableView ? 'action.disabledBackground' : 'info.main',
                color: disableView ? 'action.disabled' : 'common.white',
                '&:hover': { bgcolor: 'info.dark' },
                borderRadius: 1,
                width: 20,
                height: 20
              }}
            >
              <ViewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}

        {onEdit && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit(row.id)}
              disabled={disableEdit}
              sx={{
                bgcolor: disableEdit ? 'action.disabledBackground' : 'primary.900',
                color: disableEdit ? 'action.disabled' : 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.200' },
                borderRadius: 1, // sedikit rounded
                width: 20,
                height: 20
              }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}

        {onDelete && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => onDelete(row.id)}
              disabled={disableDelete}
              sx={{
                bgcolor: disableDelete ? 'action.disabledBackground' : 'error.main',
                color: disableDelete ? 'action.disabled' : 'common.white',
                '&:hover': { bgcolor: 'error.dark' },
                borderRadius: 1.25,
                width: 20,
                height: 20
              }}
            >
              <ClearOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Menu mode (three-dots)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <IconButton
        className="icon-action"
        size="small"
        ref={iconRef}
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
        aria-controls={anchorEl ? 'action-menu' : undefined}
      >
        <MoreVertIcon color="primary" />
      </IconButton>

      <Menu
        id="action-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 160,
            '& .MuiMenuItem-root': { fontSize: 14, py: 0.5 }
          }
        }}
      >
        {onView && (
          <MenuItem
            disabled={disableView}
            onClick={() => {
              handleClose();
              onView(row.id, row);
            }}
          >
            <ListItemIcon
              sx={{
                bgcolor: disableView ? 'action.disabledBackground' : 'info.main',
                color: disableView ? 'action.disabled' : 'common.white',
                '&:hover': { bgcolor: 'info.dark' },
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'center',
                p: 0.2,
                mr: 0.5
              }}
            >
              <ViewIcon sx={{ fontSize: 14 }} />
            </ListItemIcon>
            <ListItemText sx={{ color: 'inherit' }}>View</ListItemText>
          </MenuItem>
        )}

        {onEdit && (
          <MenuItem
            disabled={disableEdit}
            onClick={() => {
              handleClose();
              onEdit(row.id);
            }}
          >
            <ListItemIcon
              sx={{
                bgcolor: disableEdit ? 'action.disabledBackground' : 'primary.900',
                color: disableEdit ? 'action.disabled' : 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.200' },
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'center',
                p: 0.2,
                mr: 0.5
              }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </ListItemIcon>
            <ListItemText sx={{ color: 'inherit' }}>Edit</ListItemText>
          </MenuItem>
        )}

        {onDelete && (
          <MenuItem
            disabled={disableDelete}
            onClick={() => {
              handleClose();
              onDelete(row.id);
            }}
          >
            <ListItemIcon
              sx={{
                mb: 0.5,
                borderRadius: 1,
                color: disableDelete ? 'action.disabled' : 'primary.contrastText',
                bgcolor: disableDelete ? 'action.disabledBackground' : 'error.main',
                '&:hover': { bgcolor: 'error.dark' },
                display: 'flex',
                justifyContent: 'center',
                p: 0.2,
                mr: 0.5
              }}
            >
              <ClearOutlinedIcon sx={{ fontSize: 14 }} />
            </ListItemIcon>
            <ListItemText sx={{ color: 'inherit' }}>Delete</ListItemText>
          </MenuItem>
        )}

        {extraActions?.map((a, i) => (
          <MenuItem
            key={i}
            onClick={() => {
              handleClose();
              a.onClick(row);
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, display: 'flex', justifyContent: 'center' }}>{a.icon}</ListItemIcon>
            <ListItemText>{a.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ActionMenu;
